import { Resource } from "../../types/resources";
import { ResourceGroup } from "../../types/views";
import {
  TIMELINE_GROUP_HEADER_HEIGHT,
  TIMELINE_LANE_HEIGHT_PX,
  TIMELINE_LANE_VERTICAL_PADDING_PX,
  TIMELINE_RESOURCE_ROW_HEIGHT,
} from "./timelineConstants";
import { ResourceLaneAssignment } from "./timelineLanes";

export interface TimelineGroupRow {
  type: "group";
  id: string;
  label: string;
  top: number;
  height: number;
}

export interface TimelineResourceRow {
  type: "resource";
  id: string;
  resource: Resource;
  top: number;
  height: number;
  laneCount: number;
  groupId?: string;
}

export type TimelineRenderRow = TimelineGroupRow | TimelineResourceRow;

export interface TimelineRowsResult {
  rows: TimelineRenderRow[];
  resourceRows: TimelineResourceRow[];
  resourceRowMap: Map<string, TimelineResourceRow>;
  totalHeight: number;
}

const getResourceId = (resource: Resource) => String(resource.id);

/**
 * Computes the visual height of a resource row given how many concurrent
 * lanes it must accommodate. Always at least `TIMELINE_RESOURCE_ROW_HEIGHT`
 * so single-lane rows match the historical default.
 */
export const computeResourceRowHeight = (laneCount: number): number => {
  const safeLaneCount = Math.max(1, laneCount);
  const lanesHeight =
    safeLaneCount * TIMELINE_LANE_HEIGHT_PX +
    2 * TIMELINE_LANE_VERTICAL_PADDING_PX;
  return Math.max(TIMELINE_RESOURCE_ROW_HEIGHT, lanesHeight);
};

export const buildTimelineRows = (
  resources: Resource[],
  resourceGroups?: ResourceGroup[],
  resourceLaneMap?: Map<string, ResourceLaneAssignment>
): TimelineRowsResult => {
  const rows: TimelineRenderRow[] = [];
  const resourceRowMap = new Map<string, TimelineResourceRow>();
  const groupedResourceIds = new Set<string>();
  let currentTop = 0;

  const addResourceRow = (resource: Resource, groupId?: string) => {
    const laneCount =
      resourceLaneMap?.get(getResourceId(resource))?.laneCount ?? 1;
    const height = computeResourceRowHeight(laneCount);

    const row: TimelineResourceRow = {
      type: "resource",
      id: `resource-${resource.id}`,
      resource,
      top: currentTop,
      height,
      laneCount,
      groupId,
    };

    rows.push(row);
    resourceRowMap.set(getResourceId(resource), row);
    groupedResourceIds.add(getResourceId(resource));
    currentTop += height;
  };

  resourceGroups?.forEach((group) => {
    const groupResources = group.resourceIds
      .map((resourceId) =>
        resources.find((resource) => String(resource.id) === String(resourceId))
      )
      .filter(Boolean) as Resource[];

    if (!groupResources.length) {
      return;
    }

    rows.push({
      type: "group",
      id: `group-${group.id}`,
      label: group.label,
      top: currentTop,
      height: TIMELINE_GROUP_HEADER_HEIGHT,
    });
    currentTop += TIMELINE_GROUP_HEADER_HEIGHT;

    groupResources.forEach((resource) => addResourceRow(resource, group.id));
  });

  resources
    .filter((resource) => !groupedResourceIds.has(getResourceId(resource)))
    .forEach((resource) => addResourceRow(resource));

  const resourceRows = rows.filter(
    (row): row is TimelineResourceRow => row.type === "resource"
  );

  return {
    rows,
    resourceRows,
    resourceRowMap,
    totalHeight: currentTop,
  };
};
